/**
 * Opening a short-lived signed link (5 minutes) the server issued for a file. "View" opens PDF / JPEG / PNG in a new
 * tab (the server sends them inline); "Download" saves the file. The tab for View is opened synchronously on the click
 * so popup blockers allow it, then pointed at the link once it arrives (closed again if the request fails).
 */
import { useMutation } from '@tanstack/react-query';

import type { Disposition, DownloadLink } from './api/attachments';

export type FileOpenMode = 'view' | 'download';

/** Types a browser can show in a tab (the server sends them inline; everything else always downloads). */
export const VIEWABLE_TYPES: readonly string[] = ['application/pdf', 'image/jpeg', 'image/png'];

/** True when the file can be opened in a tab: by content type, else by file-name extension. */
export function isViewable(file: { contentType?: string | null; fileName?: string | null }): boolean {
  if (file.contentType) return VIEWABLE_TYPES.includes(file.contentType.split(';')[0]?.trim().toLowerCase() ?? '');
  return /\.(pdf|jpe?g|png)$/i.test(file.fileName ?? '');
}

function saveLink(link: DownloadLink) {
  if (typeof document === 'undefined') return;
  const a = document.createElement('a');
  a.href = link.url;
  a.download = link.fileName;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Opens or saves a file through `fetchLink(disposition)` (an api/ function of the owning feature). */
export async function openFile(fetchLink: (disposition: Disposition) => Promise<DownloadLink>, mode: FileOpenMode): Promise<void> {
  const tab = mode === 'view' && typeof window !== 'undefined' ? window.open('about:blank', '_blank') : null;
  if (tab) tab.opener = null;
  try {
    const link = await fetchLink(mode === 'view' ? 'inline' : 'attachment');
    if (tab) tab.location.replace(link.url);
    else if (mode === 'view' && typeof window !== 'undefined') window.open(link.url, '_blank', 'noopener');
    else saveLink(link);
  } catch (e) {
    tab?.close();
    throw e;
  }
}

/** Mutation wrapper so buttons can show pending state and errors render in place. */
export function useOpenFile() {
  return useMutation({
    mutationFn: ({ fetchLink, mode }: { key: string; fetchLink: (disposition: Disposition) => Promise<DownloadLink>; mode: FileOpenMode }) => openFile(fetchLink, mode),
  });
}
